import { ILocationResponse } from '@kcftech/prometheustypescriptapiclient';
import { CreateButton } from 'components/button/CreateButton';
import { DebouncedSearchInput } from 'components/data/DebouncedSearchInput';
import { DocumentTitle } from 'components/DocumentTitle';
import { TablePageLayout } from 'components/page/styled';
import { usePermissions } from 'contexts/PermissionsContext';
import { usePagedIntegrationsDataSource } from 'custom_hooks/data_hooks/data_source/integrationsDataSource';
import { CreateIntegrationModal } from 'pages/Settings/Integrations/CreateIntegrationModal';
import { IntegrationDetailsPage } from 'pages/Settings/Integrations/IntegrationDetailsPage';
import { IntegrationsTable } from 'pages/Settings/Integrations/IntegrationsTable';
import { SearchCreateButtonContainer } from 'pages/Settings/shared/styled';
import { useEffect, useState } from 'react';
import { Route, Switch, useRouteMatch } from 'react-router-dom';

export type IntegrationsPageProps = Readonly<{
    location: ILocationResponse;
}>;

export function IntegrationsPage({ location }: IntegrationsPageProps) {
    const { canCreateIntegrations } = usePermissions();
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const { url } = useRouteMatch();
    const dataSource = usePagedIntegrationsDataSource({
        name: 'integrations-table',
        defaultFilter: {
            locationId: location.Id,
            nameSearch: '',
        },
    });
    const { filter, setFilter, reloadPage } = dataSource;

    const onIntegrationsCreate = () => {
        setCreateModalOpen(false);
        reloadPage();
    };

    useEffect(() => {
        setFilter((currentFilter) => ({
            ...currentFilter,
            nameSearch: filter.nameSearch,
        }));
    }, [filter.nameSearch, setFilter]);

    useEffect(() => {
        setFilter((currentFilter) => ({
            ...currentFilter,
            locationId: location.Id,
        }));
    }, [location, setFilter]);

    return (
        <Switch>
            <Route
                path={`${url}/:integrationId`}
                render={({ match }) => (
                    <IntegrationDetailsPage
                        location={location}
                        integrationId={match.params.integrationId}
                        onChange={reloadPage}
                    />
                )}
            />
            {/* @ts-expect-error: type issue with older version of react-router-dom */}
            <Route default>
                <TablePageLayout>
                    <DocumentTitle title={`${location.Name} Integrations Settings`} />
                    <SearchCreateButtonContainer>
                        <DebouncedSearchInput
                            placeholder="Search"
                            value={filter.nameSearch}
                            onChange={(newNameSearch) =>
                                setFilter((currentFilter) => ({
                                    ...currentFilter,
                                    nameSearch: newNameSearch,
                                }))
                            }
                        />
                        {canCreateIntegrations && (
                            <CreateButton onClick={() => setCreateModalOpen(true)} />
                        )}
                        {createModalOpen && (
                            <CreateIntegrationModal
                                locationId={location.Id}
                                onCreate={onIntegrationsCreate}
                                onClose={() => setCreateModalOpen(false)}
                            />
                        )}
                    </SearchCreateButtonContainer>
                    <IntegrationsTable dataSource={dataSource} locationId={location.Id} />
                </TablePageLayout>
            </Route>
        </Switch>
    );
}
